import { ChangeDetectionStrategy, Component, computed, inject, input, TemplateRef, viewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { DialogService, ImageComponent } from 'common-ui';
import { ProductIdLabelComponent } from '../../../../../../product/common/basic-properties/product-id-label';
import { ProductShortDescriptionComponent } from '../../../../../../product/common/basic-properties/product-short-description';
import { ItemComponentBase } from '../../../../../ItemComponentBase';
import { ItemStockComponent } from '../../../item-stock/item-stock.component';
import { getItemStockButtonViewLocalize } from './item-stock-button-view-localize';
import { ItemStockButtonState } from './ItemStockButtonState';
import { DecimalPipe } from '@angular/common';

@Component({
  selector: 'ideal-item-stock-button-view',
  imports: [
    MatButtonModule,
    MatDialogModule,
    ItemStockComponent,
    MatIcon,
    MatTooltip,
    ProductIdLabelComponent,
    ProductShortDescriptionComponent,
    ImageComponent,
    DecimalPipe,
  ],
  standalone: true,
  templateUrl: './item-stock-button-view.component.html',
  styleUrl: './item-stock-button-view.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[attr.state]': 'stockLocation()',
    '[class.multi-line]': 'amountIsBig()',
  },
})
export class ItemStockButtonViewComponent extends ItemComponentBase {
  private readonly _dialogService = inject(DialogService);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private readonly _dialogTpl = viewChild<TemplateRef<any>>('stockPopup');
  private readonly _stateTooltip = viewChild<MatTooltip>('stateTooltip');

  public readonly stockLocation = input<ItemStockButtonState>(ItemStockButtonState.SOLD_OUT);
  public readonly stockAmount = input<number>(0);
  protected readonly showAmount = computed(() => {
    switch (this.stockLocation()) {
      case ItemStockButtonState.IN_STORE:
      case ItemStockButtonState.IN_STOCK:
      case ItemStockButtonState.WAREHOUSE:
        return this.stockAmount() > 0;
      default:
        return false;
    }
  });

  protected readonly amountIsBig = computed(() => this.showAmount() && this.stockAmount() > 99999);

  public readonly popupEnabled = computed(() => {
    switch (this.stockLocation()) {
      case ItemStockButtonState.IN_STORE:
      case ItemStockButtonState.IN_STOCK:
      case ItemStockButtonState.WAREHOUSE:
        return true;
      default:
        return false;
    }
  });

  public readonly icon = computed(() => {
    switch (this.stockLocation()) {
      case ItemStockButtonState.IN_STORE:
        return 'storefront';
      case ItemStockButtonState.IN_STOCK:
        return 'check_circle';
      case ItemStockButtonState.WAREHOUSE:
        return 'warehouse';
      case ItemStockButtonState.BACK_ORDERED:
        return 'pending';
      case ItemStockButtonState.ORDERABLE:
        return 'event_available';
      default:
        return 'block';
    }
  });

  protected readonly label = computed(
    () => getItemStockButtonViewLocalize()[this.stockLocation()] ?? getItemStockButtonViewLocalize()[ItemStockButtonState.SOLD_OUT],
  );

  protected onClick() {
    if (this.popupEnabled()) {
      this.openStockDialog();
    } else {
      // States without stock data are only clickable icon-only (see the stylesheet), where the
      // icon alone does not say enough; the label shows in a tooltip instead. Show, not toggle:
      // hover (or a tap that also fires mouseenter) may already have opened it. A tap or click
      // elsewhere closes it.
      this._stateTooltip()?.show(0);
    }
  }

  protected openStockDialog() {
    const tpl = this._dialogTpl();
    if (tpl) {
      this._dialogService.open(tpl, {
        width: '50em',
        maxHeight: 'calc(100vh - 1em)',
        maxWidth: 'calc(100vw - 1em)',
      });
    }
  }
}
